import { z } from 'zod';

import type { MapPoint } from '@/lib/maps/types';

import { DEVICE_STATUS } from './constants';

/**
 * The six `CustomerDeviceStatus` tokens the backend can send. Listed rather than taken as any
 * string so a rename lands as one loud contract-drift error instead of an unstyled marker —
 * `active` / `idle` were renamed to `running` / `off` and no longer exist.
 */
export const MAP_DEVICE_STATUSES = [
  'pending_configuration',
  'pending_confirmation',
  'pending_qc',
  'running',
  'off',
  'disconnected',
] as const;

/**
 * Where the stored position came from (113587) — the backend's whole `DeviceLocationSource` enum.
 * `remapped` is the value a Device carries the moment the Remap Device save lands (119176); listing
 * it here is what stops every remapped Device's map row degrading to `null` and firing a spurious
 * `contract_drift` report. `null` for a Device placed before the column existed.
 */
export const DEVICE_LOCATION_SOURCES = ['reported', 'installation', 'remapped'] as const;

/**
 * A GeoJSON Point — `[lng, lat]`, in that order. Normalised to `MapPoint` at the parse boundary so
 * no consumer has to remember which way round GeoJSON puts them.
 *
 * Never null: this route returns located Devices only. An **unplaced device** — one with no valid
 * stored location — is omitted from `data` entirely and counted in `meta.unplacedCount`.
 */
const geoJsonPointSchema = z
  .object({ type: z.literal('Point'), coordinates: z.tuple([z.number(), z.number()]) })
  .transform(({ coordinates: [lon, lat] }): MapPoint => ({ lat, lon }));

export const mapDeviceSchema = z.object({
  id: z.string(),
  /** The marker's label: the Grower's custom name where one is set, otherwise the actual Device
   *  name — resolved server-side, so the client never re-derives it (113586 §3b). */
  label: z.string(),
  deviceName: z.string(),
  deviceCustomName: z.string().nullable(),
  /** Extra payload. No surface shows a Device's Category — search must not match on it (113585)
   *  and the quick view omits it (113588 §4f). Modelled because the server sends it; read by
   *  nothing. */
  category: z.string(),
  /**
   * An unrecognised status must not blank the whole map layer — the backend can add a status
   * (`MAP_DEVICE_STATUSES` already carries `pending_confirmation`, which the Devices-listing
   * feature's `EFFECTIVE_STATUSES` doesn't, so the two have already drifted once) — so it degrades
   * to Pending Configuration, same conservative fallback `devices/schemas.ts` uses for
   * `effectiveStatus`. `parseContract`'s Sentry signal still reports the raw drift; this only stops
   * one bad row from taking down every device on the map.
   */
  status: z.enum(MAP_DEVICE_STATUSES).catch(DEVICE_STATUS.PENDING_CONFIGURATION),
  /** Open-ended and best-effort: an unrecognised source degrades to `null` (same as "no source
   *  recorded") rather than failing the whole row. */
  locationSource: z.enum(DEVICE_LOCATION_SOURCES).nullable().catch(null),
  /**
   * `null` ⇒ an **unassociated device**: it has a location but sits inside no Boundary. It is still
   * drawn and still clickable; it simply belongs to no Field. Singular, because Boundaries may not
   * overlap — a Device on a shared edge resolves to the more recently created Field, server-side.
   */
  fieldId: z.string().nullable(),
  location: geoJsonPointSchema,
});

/**
 * `meta.unplacedCount` is the count of **unplaced devices** — no valid stored location, so absent
 * from the map entirely while still listed on the Devices page. Reported by the server rather than
 * inferred here, and preserved through the parse so the layers can surface it.
 */
export const mapDeviceListResponseSchema = z.object({
  data: z.array(mapDeviceSchema),
  meta: z.object({ totalItems: z.number(), unplacedCount: z.number() }),
});

export type MapDevice = z.infer<typeof mapDeviceSchema>;
export type MapDeviceListResponse = z.infer<typeof mapDeviceListResponseSchema>;
export type MapDeviceStatus = (typeof MAP_DEVICE_STATUSES)[number];
export type DeviceLocationSource = (typeof DEVICE_LOCATION_SOURCES)[number];
